<template>
  <transition name="admin-alert-slide-up">
    <div 
      v-if="shouldShowAlert && !isDismissed"
      class="fixed bottom-16 left-0 right-0 z-[999]"
    >
      <div 
        :class="alertClasses"
        class="shadow-lg py-3 mx-4 mb-4 rounded-xl"
      >
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <div class="flex items-center">
            <svg class="h-5 w-5 mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path v-if="alertType === 'info'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              <path v-if="alertType === 'success'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              <path v-if="alertType === 'warning'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              <path v-if="alertType === 'error'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p class="text-sm md:text-base font-medium">
              {{ alertMessage }}
            </p>
          </div>
          
          <button
            @click="dismissAlert"
            class="ml-4 flex-shrink-0 inline-flex items-center justify-center h-8 w-8 rounded-full focus:outline-none transition-all duration-200"
            :class="closeButtonClasses"
            aria-label="Dismiss alert"
          >
            <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { mapState } from 'vuex';

export default {
  name: 'AdminAlerts',
  data() {
    return {
      isDismissed: false
    };
  },
  computed: {
    ...mapState(['globalSettings']),
    shouldShowAlert() {
      return this.globalSettings?.superAlert?.showAlert && 
             this.globalSettings?.superAlert?.message !== '';
    },
    alertMessage() {
      return this.globalSettings?.superAlert?.message || '';
    },
    alertType() {
      return this.globalSettings?.superAlert?.alertType || 'info';
    },
    alertStorageKey() {
      // Create unique key based on alert message and type
      return `alert-dismissed-${this.alertType}-${this.alertMessage.hashCode()}`;
    },
    alertClasses() {
      const base = 'text-white';
      const typeMap = {
        info: 'bg-gradient-to-r from-blue-500 to-indigo-600',
        success: 'bg-gradient-to-r from-green-500 to-emerald-600',
        warning: 'bg-gradient-to-r from-amber-500 to-yellow-600',
        error: 'bg-gradient-to-r from-rose-500 to-red-600'
      };
      return `${base} ${typeMap[this.alertType] || typeMap.info}`;
    },
    closeButtonClasses() {
      return {
        info: 'text-blue-100 hover:text-white hover:bg-blue-600/30',
        success: 'text-green-100 hover:text-white hover:bg-green-600/30',
        warning: 'text-amber-100 hover:text-white hover:bg-amber-600/30',
        error: 'text-rose-100 hover:text-white hover:bg-rose-600/30'
      }[this.alertType];
    }
  },
  created() {
    // Check sessionStorage on component creation
    this.checkDismissedStatus();
  },
  watch: {
    // Watch for alert changes
    alertMessage() {
      this.checkDismissedStatus();
    }
  },
  methods: {
    checkDismissedStatus() {
      this.isDismissed = sessionStorage.getItem(this.alertStorageKey) === 'true';
    },
    dismissAlert() {
      // Store dismissal in sessionStorage
      sessionStorage.setItem(this.alertStorageKey, 'true');
      this.isDismissed = true;
    }
  }
};

// Helper function to create simple hash from string
String.prototype.hashCode = function() {
  let hash = 0;
  for (let i = 0; i < this.length; i++) {
    const char = this.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash |= 0; // Convert to 32bit integer
  }
  return hash;
};
</script>

<style scoped>
.admin-alert-slide-up-enter-active,
.admin-alert-slide-up-leave-active {
  transition: all 0.3s ease;
}
.admin-alert-slide-up-enter-from,
.admin-alert-slide-up-leave-to {
  opacity: 0;
  transform: translateY(100%);
}
</style>